import { FC } from 'react';
import { Card, Col, InputNumber, Row, Select, Switch, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { NAMESPACE } from '@/constants';
import type { IoPricingSettings, IoRate } from '@/api';

const { Text } = Typography;

interface Props {
  value: IoPricingSettings;
  onChange: (next: IoPricingSettings) => void;
}

const CURRENCIES = ['GBP', 'EUR', 'USD'];

/**
 * Commercial settings for a template's totals.
 *
 * Discount is applied to the line totals. Tax is not configurable here: the
 * figures an IO quotes come from the deal in Pipedrive and are stated exclusive
 * of VAT, so a rate typed against a template would only ever disagree with the
 * source. The underlying fields stay on the model so templates saved with a
 * rate keep rendering what they already had.
 */
export const PricingSettings: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);

  const rate = (key: 'discount', label: string) => {
    const current: IoRate = value[key] ?? { value: 0, type: 'percent' };
    return (
      <Col xs={24} md={8}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Text>
        <Space.Compact block style={{ marginTop: 4 }}>
          <InputNumber
            min={0}
            step={0.5}
            value={current.value}
            aria-label={label}
            onChange={(next) => onChange({ ...value, [key]: { ...current, value: Number(next) || 0 } })}
            style={{ width: '60%' }}
          />
          <Select
            value={current.type}
            onChange={(next) => onChange({ ...value, [key]: { ...current, type: next } })}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            style={{ width: '40%' }}
            options={[
              { label: '%', value: 'percent' },
              { label: t('pricingAbsolute'), value: 'absolute' }
            ]}
          />
        </Space.Compact>
      </Col>
    );
  };

  return (
    <Card size="small" title={t('pricingTitle')}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('pricingCurrency')}
          </Text>
          <Select
            value={value.currency}
            onChange={(currency) => onChange({ ...value, currency })}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            options={CURRENCIES.map((code) => ({ label: code, value: code }))}
            style={{ width: '100%', marginTop: 4 }}
          />
        </Col>
        {rate('discount', t('pricingDiscount'))}
        <Col xs={24} md={8}>
          <Space>
            <Switch
              checked={value.showDiscountRow}
              onChange={(showDiscountRow) => onChange({ ...value, showDiscountRow })}
            />
            <Text>{t('pricingShowDiscount')}</Text>
          </Space>
        </Col>
        <Col xs={24} md={8}>
          <Space>
            <Switch
              checked={value.allowLineDiscount}
              onChange={(allowLineDiscount) => onChange({ ...value, allowLineDiscount })}
            />
            <Text>{t('pricingLineDiscount')}</Text>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default PricingSettings;
